/**
 * Changing many accounts, or many cheques, at once.
 *
 * The change is made by `customers_bulk_update()` / `cheques_bulk_status()`
 * (migration 0033), which check the capability, then every row against the
 * caller's scope, and write the history in the same transaction. This file is
 * the product side: the limits, the words for each change, and the sentence the
 * confirmation shows from a dry run — the same function with nothing written —
 * so what a person confirms is what the database said it would do.
 */

/** The most accounts one change can reach, as the database enforces. */
export const BULK_LIMIT = 500;

/** The most cheques one change can reach, as the database enforces. */
export const CHEQUE_BULK_LIMIT = 200;

export type BulkAction = 'collector' | 'follow_up' | 'rank';

export interface BulkResult {
    requested: number;
    changed: number;
    unchanged: number;
    /** Outside the person's scope, in another company, or no longer there. */
    unavailable: number;
    /** Owes nothing, so a date would chase nothing. */
    settled: number;
    /** Marked collected, so a date would chase nothing. */
    collected: number;
    /** Net outstanding of the accounts that change. */
    amount: number;
}

export interface ChequeBulkResult {
    requested: number;
    changed: number;
    /** The move is not one a bank allows from where the cheque stands. */
    notAllowed: number;
    unavailable: number;
    amount: number;
}

const num = (value: unknown): number => Number(value) || 0;

export function readBulkResult(value: unknown): BulkResult {
    const row = (value ?? {}) as Record<string, unknown>;
    return {
        requested: num(row['requested']),
        changed: num(row['changed']),
        unchanged: num(row['unchanged']),
        unavailable: num(row['unavailable']),
        settled: num(row['settled']),
        collected: num(row['collected']),
        amount: num(row['amount']),
    };
}

export function readChequeBulkResult(value: unknown): ChequeBulkResult {
    const row = (value ?? {}) as Record<string, unknown>;
    return {
        requested: num(row['requested']),
        changed: num(row['changed']),
        notAllowed: num(row['notAllowed']),
        unavailable: num(row['unavailable']),
        amount: num(row['amount']),
    };
}

const accounts = (n: number) => (n === 1 ? '1 account' : `${n} accounts`);
const cheques = (n: number) => (n === 1 ? '1 cheque' : `${n} cheques`);

/**
 * The lines under "will change", in the order a person needs them: what
 * happens, then what is left alone and why. Nothing is said about an account
 * that is unavailable beyond the count — which ids were real is not the
 * screen's to confirm.
 */
export function bulkSkipLines(result: BulkResult): string[] {
    const lines: string[] = [];
    if (result.unchanged > 0) lines.push(`${accounts(result.unchanged)} already ${result.unchanged === 1 ? 'has' : 'have'} this, and ${result.unchanged === 1 ? 'is' : 'are'} left as ${result.unchanged === 1 ? 'it is' : 'they are'}.`);
    if (result.settled > 0) lines.push(`${accounts(result.settled)} ${result.settled === 1 ? 'owes' : 'owe'} nothing, so no date is set.`);
    if (result.collected > 0) lines.push(`${accounts(result.collected)} ${result.collected === 1 ? 'is' : 'are'} marked collected, so no date is set.`);
    if (result.unavailable > 0) lines.push(`${accounts(result.unavailable)} ${result.unavailable === 1 ? 'is' : 'are'} not available to you and will not change.`);
    return lines;
}

export function chequeBulkSkipLines(result: ChequeBulkResult): string[] {
    const lines: string[] = [];
    if (result.notAllowed > 0) lines.push(`${cheques(result.notAllowed)} cannot make that move from where ${result.notAllowed === 1 ? 'it stands' : 'they stand'}, and will not change.`);
    if (result.unavailable > 0) lines.push(`${cheques(result.unavailable)} ${result.unavailable === 1 ? 'is' : 'are'} not available to you and will not change.`);
    return lines;
}

export const bulkChangedLine = (result: BulkResult): string =>
    result.changed === 0 ? 'Nothing would change.' : `${accounts(result.changed)} will change.`;

export const chequeBulkChangedLine = (result: ChequeBulkResult): string =>
    result.changed === 0 ? 'Nothing would change.' : `${cheques(result.changed)} will change.`;

/** What the notice says after a change was made. */
export function bulkDoneLine(result: BulkResult, what: string): string {
    if (result.changed === 0) return 'Nothing changed.';
    const rest = result.requested - result.changed;
    return `${what} on ${accounts(result.changed)}.${rest > 0 ? ` ${accounts(rest)} left as ${rest === 1 ? 'it was' : 'they were'}.` : ''}`;
}

export function chequeBulkDoneLine(result: ChequeBulkResult, what: string): string {
    if (result.changed === 0) return 'Nothing changed.';
    const rest = result.requested - result.changed;
    return `${what}: ${cheques(result.changed)}.${rest > 0 ? ` ${cheques(rest)} left as ${rest === 1 ? 'it was' : 'they were'}.` : ''}`;
}
